// Canonical WhatsApp template placeholders — the single source shared by the template
// CREATE editor (which offers them as insertable pills) and the SEND composer (which
// labels + pre-fills each positional variable from the linked member/purchase). Keeping
// both surfaces on this one list is what lets the composer show "Customer name" instead of
// "{{1}}" and populate it automatically.
//
// Each entry has:
//   key      stable identifier persisted with the template (survives locale changes)
//   label    localized, human name shown in the UI
//   example  sample value for the create-form preview
//   shopOnly   order-related fields only make sense when the shop extension is enabled
//   ticketsOnly likewise for the ticket desk's own fields
//   resolve  reads the real value from a linked { member, purchase, ticket } (+ timestamp2date)
//
// `context` supplies: t (the $t translator), hasShop, hasMembers, hasTickets.

// The link token a WhatsApp/Email send swaps for the linked order's signed storefront URL
// (CommunicationLinkResolver). In a TEMPLATE button it marks a dynamic per-order URL button:
// the backend bakes the storefront base + {{1}} and fills the signed suffix at send time
// (order.details_url_suffix). MUST match the backend constant
// App\Services\Communication\CommunicationLinkResolver::TOKEN_ORDER_DETAILS_URL.
export const ORDER_DETAILS_TOKEN = '{{order.details_url}}'

// The button variable key the backend expects for a dynamic order-details link button.
// MUST match App\Services\Communication\CommunicationLinkResolver::TOKEN_KEY_ORDER_DETAILS_URL_SUFFIX.
export const ORDER_DETAILS_SUFFIX_KEY = 'order.details_url_suffix'

// The "Track shipment" button. It bakes the API /track base (not a storefront domain),
// so the API 301s to the carrier and it works for every tenant with no storefront route.
// The token marks the picked link; the suffix key is what the backend bakes + fills.
// MUST match CommunicationLinkResolver::TOKEN_KEY_ORDER_TRACKING_REDIRECT_SUFFIX.
export const ORDER_TRACKING_TOKEN = '{{order.tracking_url}}'

// The marker a {{ Ticket number }} body variable travels as. The value is minted
// server-side (a ticket number does not exist until the ticket is saved, and a
// reply never carries it). MUST match
// App\Services\Communication\CommunicationLinkResolver::TOKEN_TICKET_NUMBER.
export const TICKET_NUMBER_TOKEN = '{{ticket.number}}'
export const ORDER_TRACKING_SUFFIX_KEY = 'order.tracking_redirect_suffix'

export const whatsappPlaceholders = ({ t, hasShop, hasMembers }) => {
    const recipientLabel = hasShop
        ? t('Customer name')
        : (hasMembers ? t('Member name') : t('Recipient name'))

    return [
        {
            key: 'recipient.name',
            label: recipientLabel,
            example: 'Max Mustermann',
            shopOnly: false,
            resolve: ({ member }) => member
                ? ([member.first_name, member.last_name].filter(Boolean).join(' ') || member.name || '')
                : ''
        },
        {
            key: 'ticket.number',
            label: t('Ticket number'),
            example: 'T-1042',
            ticketsOnly: true,
            // The same reference the requester sees in every ticket mail subject.
            resolve: ({ ticket }) => ticket ? (ticket.number || (ticket.id ? 'T-' + ticket.id : '')) : ''
        },
        {
            key: 'order.number',
            label: t('Order number'),
            example: '#10234',
            shopOnly: true,
            // Same reference the details panel shows: the invoice number, else #<id>.
            resolve: ({ purchase }) => purchase ? (purchase.invoice_nr || ('#' + purchase.id)) : ''
        },
        {
            key: 'order.date',
            label: t('Order date'),
            example: '15.07.2026',
            shopOnly: true,
            resolve: ({ purchase, timestamp2date }) =>
                (purchase && purchase.created_at && timestamp2date) ? timestamp2date(purchase.created_at) : ''
        },
        {
            key: 'order.status',
            label: t('Order status'),
            example: 'Versandbereit',
            shopOnly: true,
            resolve: ({ purchase }) => (purchase && purchase.status && purchase.status.name) || ''
        },
        {
            key: 'order.tracking',
            label: t('Tracking number'),
            example: 'DHL 00340434290',
            shopOnly: true,
            // No shipment-tracking source on the conversation yet — labelled, filled by hand.
            resolve: () => ''
        },
        {
            key: 'order.amount',
            label: t('Amount'),
            example: '49,90 €',
            shopOnly: true,
            resolve: ({ purchase }) =>
                (purchase && purchase.grand_total !== null && purchase.grand_total !== undefined)
                    ? [purchase.grand_total, purchase.currency].filter(Boolean).join(' ')
                    : ''
        }
    ]
}

// The subset offered in the CREATE editor: a field is offered only where the feature
// that fills it is actually sold.
export const whatsappPlaceholderOptions = (context) =>
    whatsappPlaceholders(context)
        .filter(placeholder => !placeholder.shopOnly || context.hasShop)
        .filter(placeholder => !placeholder.ticketsOnly || context.hasTickets)

// A key -> placeholder lookup for the SEND composer (every key, so a stored template's
// variables resolve even if the current CMS wouldn't offer that field for new templates).
export const whatsappPlaceholderByKey = (context) =>
    whatsappPlaceholders(context).reduce((map, placeholder) => {
        map[placeholder.key] = placeholder
        return map
    }, {})
